<template>
  <div class="reported-events-page">
    <!-- Floating Header Section -->
    <div class="page-header">
      <div class="header-glass">
        <div class="header-content">
          <div class="header-main">
            <div class="title-section">
              <div class="icon-wrapper">
                <v-icon class="header-icon">mdi-shield-alert</v-icon>
              </div>
              <div class="title-content">
                <h1 class="page-title">Reports Dashboard</h1>
                <p class="page-subtitle">
                  Advanced community protection system
                </p>
              </div>
            </div>

            <!-- Quick Actions -->
            <div class="quick-actions">
              <v-btn
                class="action-btn glass-btn"
                color="white"
                text
                @click="refreshData"
                :loading="loading"
              >
                <v-icon left>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </div>
          </div>

          <!-- Modern Stats Cards -->
          <div class="stats-grid">
            <div
              class="stat-card glass-card"
              v-for="(stat, index) in stats"
              :key="index"
            >
              <div class="stat-background"></div>
              <div class="stat-icon">
                <v-icon>{{ stat.icon }}</v-icon>
              </div>
              <div class="stat-content">
                <span class="stat-number">{{ stat.value }}</span>
                <span class="stat-label">{{ stat.label }}</span>
              </div>
              <div class="stat-glow"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modern Filters Section -->
    <div class="filters-section">
      <div class="filters-card glass-card">
        <div class="filters-content">
          <div class="filters-row">
            <div class="search-container">
              <v-text-field
                v-model="searchQuery"
                prepend-inner-icon="mdi-magnify"
                label="Search reports..."
                outlined
                dense
                hide-details
                class="search-field modern-input"
                @input="filterEvents"
              ></v-text-field>
            </div>

            <v-select
              v-model="filterType"
              :items="filterOptions"
              label="Filter by source"
              outlined
              dense
              hide-details
              class="filter-select modern-input"
              @change="filterEvents"
            ></v-select>

            <v-select
              v-model="sortBy"
              :items="sortOptions"
              label="Sort by"
              outlined
              dense
              hide-details
              class="sort-select modern-input"
              @change="sortEvents"
            ></v-select>

            <!-- View Toggle -->
            <div class="view-toggle">
              <v-btn-toggle
                v-model="viewMode"
                exclusive
                mandatory
                class="modern-toggle"
              >
                <v-btn value="grid" icon class="toggle-btn">
                  <v-icon>mdi-view-grid</v-icon>
                </v-btn>
                <v-btn value="table" icon class="toggle-btn">
                  <v-icon>mdi-table</v-icon>
                </v-btn>
              </v-btn-toggle>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Content Area -->
    <div class="content-area">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state glass-card">
        <div class="loading-animation">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            width="4"
          ></v-progress-circular>
          <div class="loading-pulse"></div>
        </div>
        <p class="loading-text">Loading community reports...</p>
      </div>

      <!-- Grid View -->
      <div v-else-if="viewMode === 'grid'" class="grid-view">
        <transition-group name="card-float" tag="div" class="cards-grid">
          <div
            v-for="event in paginatedEvents"
            :key="event._id"
            class="event-card modern-card"
            @click="viewDetails(event)"
          >
            <!-- Card Background Effects -->
            <div class="card-bg-gradient"></div>
            <div class="card-highlight"></div>

            <!-- Card Header -->
            <div class="card-header">
              <div class="user-info">
                <div class="avatar-container">
                  <v-avatar size="42" class="user-avatar modern-avatar">
                    <v-img
                      :src="event.poster?.midThumbnail || getDefaultAvatar()"
                      :alt="getUserName(event)"
                    />
                  </v-avatar>
                  <div class="avatar-ring"></div>
                </div>
                <div class="user-details">
                  <h4 class="user-name">{{ getUserName(event) }}</h4>
                  <div class="user-meta">
                    <v-chip
                      :color="
                        event.poster_type === 'Volunteer' ? 'success' : 'info'
                      "
                      x-small
                      dark
                      class="type-chip modern-chip"
                    >
                      {{ event.poster_type }}
                    </v-chip>
                    <span class="post-date">{{
                      getRelativeTime(event.createdAt)
                    }}</span>
                  </div>
                </div>
              </div>

              <div class="reports-badge-container">
                <v-chip
                  :color="getReportsCountColor(event.reported.length)"
                  small
                  dark
                  class="reports-badge pulse-badge"
                >
                  {{ event.reported.length }}
                </v-chip>
              </div>
            </div>

            <!-- Card Content -->
            <div class="card-content">
              <p class="event-text">{{ truncateText(event.text, 120) }}</p>

              <!-- Media Preview -->
              <div
                v-if="event.files && event.files.length"
                class="media-preview modern-media"
              >
                <v-img
                  :src="event.files[0].url"
                  height="140"
                  class="preview-image"
                  cover
                >
                  <div
                    v-if="event.files.length > 1"
                    class="media-count glass-badge"
                  >
                    +{{ event.files.length - 1 }}
                  </div>
                </v-img>
              </div>
            </div>

            <!-- Card Footer -->
            <div class="card-footer">
              <div class="reporters-preview">
                <div class="reporters-avatars">
                  <v-avatar
                    v-for="(report, index) in event.reported.slice(0, 3)"
                    :key="index"
                    :size="28"
                    class="reporter-avatar floating-avatar"
                    :style="{
                      zIndex: 3 - index,
                      marginLeft: index > 0 ? '-10px' : '0'
                    }"
                  >
                    <v-img
                      :src="report.reporter?.midThumbnail || getDefaultAvatar()"
                      :alt="getReporterName(report)"
                    />
                  </v-avatar>
                </div>
                <span class="reporters-text">
                  {{ event.reported.length }} reporter{{
                    event.reported.length > 1 ? "s" : ""
                  }}
                </span>
              </div>

              <div class="card-actions">
                <v-btn
                  icon
                  small
                  @click.stop="resolveReport(event)"
                  class="action-icon success-action"
                >
                  <v-icon color="success" size="20">mdi-check</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  @click.stop="onDeleteEvent(event)"
                  class="action-icon danger-action"
                >
                  <v-icon color="error" size="20">mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </transition-group>
      </div>

      <!-- Table View -->
      <div v-else class="table-view">
        <div class="table-container glass-card">
          <v-data-table
            :headers="tableHeaders"
            :items="paginatedEvents"
            :items-per-page="itemsPerPage"
            hide-default-footer
            class="modern-table"
            @click:row="viewDetails"
          >
            <!-- User Column -->
            <template v-slot:item.user="{ item }">
              <div class="table-user">
                <v-avatar size="36" class="table-avatar">
                  <v-img
                    :src="item.poster?.midThumbnail || getDefaultAvatar()"
                    :alt="getUserName(item)"
                  />
                </v-avatar>
                <div class="table-user-info">
                  <div class="table-user-name">{{ getUserName(item) }}</div>
                  <v-chip
                    :color="
                      item.poster_type === 'Volunteer' ? 'success' : 'info'
                    "
                    x-small
                    dark
                    class="table-type-chip"
                  >
                    {{ item.poster_type }}
                  </v-chip>
                </div>
              </div>
            </template>

            <!-- Content Column -->
            <template v-slot:item.content="{ item }">
              <div class="table-content">
                <p class="table-text">{{ truncateText(item.text, 80) }}</p>
                <div
                  v-if="item.files && item.files.length"
                  class="table-media-indicator"
                >
                  <v-icon small color="primary">mdi-image</v-icon>
                  <span
                    >{{ item.files.length }} file{{
                      item.files.length > 1 ? "s" : ""
                    }}</span
                  >
                </div>
              </div>
            </template>

            <!-- Reports Column -->
            <template v-slot:item.reports="{ item }">
              <div class="table-reports">
                <v-chip
                  :color="getReportsCountColor(item.reported.length)"
                  small
                  dark
                  class="table-reports-badge"
                >
                  {{ item.reported.length }}
                </v-chip>
                <div class="table-reporters">
                  <v-avatar
                    v-for="(report, index) in item.reported.slice(0, 2)"
                    :key="index"
                    :size="24"
                    class="table-reporter-avatar"
                    :style="{ marginLeft: index > 0 ? '-8px' : '0' }"
                  >
                    <v-img
                      :src="report.reporter?.midThumbnail || getDefaultAvatar()"
                      :alt="getReporterName(report)"
                    />
                  </v-avatar>
                  <span v-if="item.reported.length > 2" class="more-reporters">
                    +{{ item.reported.length - 2 }}
                  </span>
                </div>
              </div>
            </template>

            <!-- Date Column -->
            <template v-slot:item.date="{ item }">
              <div class="table-date">
                <div class="date-primary">
                  {{ getRelativeTime(item.createdAt) }}
                </div>
                <div class="date-secondary">
                  {{ formatDate(item.createdAt) }}
                </div>
              </div>
            </template>

            <!-- Actions Column -->
            <template v-slot:item.actions="{ item }">
              <div class="table-actions">
                <v-btn
                  icon
                  small
                  @click.stop="resolveReport(item)"
                  class="table-action-btn success-action"
                >
                  <v-icon color="success" size="18">mdi-check</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  @click.stop="onDeleteEvent(item)"
                  class="table-action-btn danger-action"
                >
                  <v-icon color="error" size="18">mdi-delete</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="!loading && filteredEvents.length === 0"
        class="empty-state glass-card"
      >
        <div class="empty-icon">
          <v-icon size="80" color="grey lighten-1">mdi-shield-check</v-icon>
          <div class="empty-glow"></div>
        </div>
        <h3>No reports found</h3>
        <p>All clear! No community reports match your current filters.</p>
      </div>
    </div>

    <!-- Modern Pagination -->
    <div
      v-if="filteredEvents.length > itemsPerPage"
      class="pagination-container"
    >
      <div class="pagination-wrapper glass-card">
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="7"
          class="modern-pagination"
          circle
        ></v-pagination>
      </div>
    </div>

    <!-- Enhanced Report Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="1000px" scrollable>
      <v-card class="report-dialog modern-dialog">
        <!-- Dialog Header with Gradient -->
        <div class="dialog-header-modern">
          <div class="dialog-bg-gradient"></div>
          <div class="dialog-content-header">
            <div class="dialog-title">
              <v-icon left color="white" size="24">mdi-shield-alert</v-icon>
              Report Details
            </div>
            <v-btn icon dark @click="detailsDialog = false" class="close-btn">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-card-text class="dialog-body-modern">
          <div v-if="selectedEvent" class="report-details">
            <!-- Original Event Section -->
            <div class="detail-section">
              <h3 class="section-title-modern">
                <div class="section-icon">
                  <v-icon color="primary">mdi-post</v-icon>
                </div>
                Reported Content
              </h3>
              <div class="event-detail-card modern-detail-card">
                <div class="detail-card-bg"></div>
                <div class="event-detail-header">
                  <div class="detail-avatar-container">
                    <v-avatar size="56" class="detail-avatar">
                      <v-img
                        :src="
                          selectedEvent.poster?.midThumbnail ||
                            getDefaultAvatar()
                        "
                        :alt="getUserName(selectedEvent)"
                      />
                    </v-avatar>
                    <div class="detail-avatar-ring"></div>
                  </div>
                  <div class="event-detail-meta">
                    <h4>{{ getUserName(selectedEvent) }}</h4>
                    <div class="meta-row">
                      <v-chip
                        :color="
                          selectedEvent.poster_type === 'Volunteer'
                            ? 'success'
                            : 'info'
                        "
                        small
                        dark
                        class="detail-type-chip"
                      >
                        {{ selectedEvent.poster_type }}
                      </v-chip>
                      <span class="detail-date">{{
                        formatDate(selectedEvent.createdAt)
                      }}</span>
                    </div>
                  </div>
                </div>

                <p class="event-detail-text">{{ selectedEvent.text }}</p>

                <div
                  v-if="selectedEvent.files && selectedEvent.files.length"
                  class="event-detail-media"
                >
                  <div class="media-grid">
                    <div
                      v-for="(file, index) in selectedEvent.files"
                      :key="index"
                      class="media-item-container"
                    >
                      <v-img
                        :src="file.url"
                        max-height="200"
                        contain
                        class="detail-media-item"
                      ></v-img>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Reports List Section -->
            <div class="detail-section">
              <h3 class="section-title-modern">
                <div class="section-icon">
                  <v-icon color="warning">mdi-flag</v-icon>
                </div>
                Reports ({{ selectedEvent.reported.length }})
              </h3>
              <div class="reports-list">
                <div
                  v-for="(report, index) in selectedEvent.reported"
                  :key="index"
                  class="report-detail-item modern-report-item"
                >
                  <div class="report-item-bg"></div>
                  <div class="report-detail-header">
                    <div class="report-avatar-container">
                      <v-avatar size="40" class="report-avatar">
                        <v-img
                          :src="
                            report.reporter?.midThumbnail || getDefaultAvatar()
                          "
                          :alt="getReporterName(report)"
                        />
                      </v-avatar>
                    </div>
                    <div class="report-detail-info">
                      <h5>{{ getReporterName(report) }}</h5>
                      <div class="report-detail-meta">
                        <span class="report-detail-date">{{
                          formatDate(report.createdAt)
                        }}</span>
                        <v-chip
                          small
                          :color="getReasonColor(report.reason)"
                          dark
                          class="reason-chip modern-reason-chip"
                        >
                          {{ report.reason }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions-modern">
          <v-btn text @click="detailsDialog = false" class="modern-btn">
            Close
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            text
            @click="onDeleteEvent(selectedEvent)"
            class="modern-btn dangr-btn"
          >
            <v-icon left small>mdi-delete</v-icon>
            Delete Event
          </v-btn>
          <v-btn
            color="success"
            @click="resolveReport(selectedEvent)"
            class="modern-btn sucess-btn"
            elevated
          >
            <v-icon left small>mdi-check</v-icon>
            Mark Resolved
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import dateFormat from "dateformat";

export default {
  name: "ReportedEventsPage",
  data() {
    return {
      loading: false,
      events: [],
      filteredEvents: [],
      searchQuery: "",
      filterType: "all",
      sortBy: "newest",
      currentPage: 1,
      itemsPerPage: 12,
      detailsDialog: false,
      selectedEvent: null,
      viewMode: "grid", // 'grid' or 'table'

      filterOptions: [
        { text: "All Reports", value: "all" },
        { text: "From Volunteers", value: "volunteer" },
        { text: "From Agencies", value: "agency" }
      ],

      sortOptions: [
        { text: "Newest First", value: "newest" },
        { text: "Oldest First", value: "oldest" },
        { text: "Most Reports", value: "reports" }
      ],

      tableHeaders: [
        { text: "User", value: "user", sortable: false, width: "200px" },
        { text: "Content", value: "content", sortable: false, width: "300px" },
        { text: "Reports", value: "reports", sortable: false, width: "150px" },
        { text: "Date", value: "date", sortable: false, width: "150px" },
        { text: "Actions", value: "actions", sortable: false, width: "120px" }
      ]
    };
  },

  computed: {
    stats() {
      return [
        {
          icon: "mdi-shield-alert",
          value: this.events.length,
          label: "Total Reports"
        },
        {
          icon: "mdi-account-heart",
          value: this.volunteerReportsCount,
          label: "From Volunteers"
        },
        {
          icon: "mdi-office-building",
          value: this.agencyReportsCount,
          label: "From Agencies"
        },
        {
          icon: "mdi-clock-alert",
          value: this.urgentReportsCount,
          label: "Urgent Cases"
        }
      ];
    },

    totalPages() {
      return Math.ceil(this.filteredEvents.length / this.itemsPerPage);
    },

    paginatedEvents() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredEvents.slice(start, end);
    },

    volunteerReportsCount() {
      return this.events.filter(event =>
        event.reported.some(report => report.poster_type === "Volunteer")
      ).length;
    },

    agencyReportsCount() {
      return this.events.filter(event =>
        event.reported.some(report => report.poster_type === "Agency")
      ).length;
    },

    urgentReportsCount() {
      return this.events.filter(event => event.reported.length >= 3).length;
    }
  },

  methods: {
    ...mapActions("event", {
      getReportedEvents: "getReportedEvents",
      resolveReportedEvent: "resolveReportedEvent",
      deleteReportedEvent: "deleteEvent"
    }),

    async fetchEvents() {
      this.loading = true;
      try {
        const response = await this.getReportedEvents();
        this.events = response.data || response;
        this.filterEvents();
      } catch (error) {
        console.error("Error fetching events:", error);
        this.$toast.error("Failed to load reports");
      } finally {
        this.loading = false;
      }
    },

    async refreshData() {
      await this.fetchEvents();
      this.$toast.success("Reports refreshed");
    },

    filterEvents() {
      let filtered = [...this.events];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          event =>
            event.text.toLowerCase().includes(query) ||
            this.getUserName(event)
              .toLowerCase()
              .includes(query)
        );
      }

      // Type filter
      if (this.filterType !== "all") {
        filtered = filtered.filter(event => {
          if (this.filterType === "volunteer") {
            return event.reported.some(
              report => report.poster_type === "Volunteer"
            );
          } else if (this.filterType === "agency") {
            return event.reported.some(
              report => report.poster_type === "Agency"
            );
          }
          return true;
        });
      }

      this.filteredEvents = filtered;
      this.sortEvents();
      this.currentPage = 1;
    },

    sortEvents() {
      this.filteredEvents.sort((a, b) => {
        switch (this.sortBy) {
          case "newest":
            return new Date(b.createdAt) - new Date(a.createdAt);
          case "oldest":
            return new Date(a.createdAt) - new Date(b.createdAt);
          case "reports":
            return b.reported.length - a.reported.length;
          default:
            return 0;
        }
      });
    },

    viewDetails(event) {
      this.selectedEvent = event;
      this.detailsDialog = true;
    },

    async resolveReport(event) {
      try {
        await this.resolveReportedEvent({ _id: event._id });
        this.events = this.events.filter(e => e._id !== event._id);
        this.filterEvents();
        if (this.detailsDialog) this.detailsDialog = false;
        this.$toast.success("Report resolved successfully");
      } catch (error) {
        console.error("Error resolving report:", error);
        this.$toast.error("Failed to resolve report");
      }
    },
    onDeleteEvent(event) {
      this.deleteEvent(event);
    },
    onDeleted(event) {
      this.events = this.events.filter(e => e._id != event._id);
      this.filterEvents(); // Update filtered events
      if (this.detailsDialog) this.detailsDialog = false;
    },

    async deleteEvent(event) {
      if (
        !confirm(
          "Are you sure you want to delete this event? This action cannot be undone."
        )
      ) {
        return;
      }

      try {
        await this.deleteReportedEvent({ _id: event._id });
        this.events = this.events.filter(e => e._id !== event._id);
        this.filterEvents();
        if (this.detailsDialog) this.detailsDialog = false;
        this.$toast.success("Event deleted successfully");
      } catch (error) {
        console.error("Error deleting event:", error);
        this.$toast.error("Failed to delete event");
      }
    },

    getUserName(event) {
      return event.poster_type === "Volunteer"
        ? `${event.poster.first_name} ${event.poster.last_name}`
        : event.poster.name;
    },

    getReporterName(report) {
      return report.poster_type === "Volunteer"
        ? `${report.reporter.first_name} ${report.reporter.last_name}`
        : report.reporter.name;
    },

    formatDate(date) {
      if (!date) return "";
      return dateFormat(new Date(date), "mmm dd, yyyy 'at' h:MM TT");
    },

    getRelativeTime(date) {
      if (!date) return "";
      const now = new Date();
      const eventDate = new Date(date);
      const diffTime = Math.abs(now - eventDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays}d ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
      return `${Math.floor(diffDays / 30)}mo ago`;
    },

    getDefaultAvatar() {
      return "/default-avatar.png";
    },

    getReportsCountColor(count) {
      if (count >= 5) return "error";
      if (count >= 3) return "warning";
      if (count >= 2) return "orange";
      return "info";
    },

    getReasonColor(reason) {
      const colors = {
        "Suspicious, spam, or fake/Spam": "warning",
        "Intellectual property infringement or defamation": "error",
        "Harassment or bullying": "purple",
        "Violence or harmful behavior": "red darken-2",
        Other: "grey"
      };
      return colors[reason] || "grey";
    },

    truncateText(text, limit) {
      if (!text) return "";
      return text.length > limit ? text.substring(0, limit) + "..." : text;
    }
  },

  mounted() {
    this.fetchEvents();
  }
};
</script>

<style scoped>
/* Modern CSS Variables */
:root {
  --primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  --secondary-gradient: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  --success-gradient: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  --glass-bg: rgba(255, 255, 255, 0.1);
  --glass-border: rgba(255, 255, 255, 0.2);
  --shadow-soft: 0 8px 32px rgba(0, 0, 0, 0.1);
  --shadow-medium: 0 12px 40px rgba(0, 0, 0, 0.15);
  --shadow-strong: 0 20px 60px rgba(0, 0, 0, 0.2);
}

/* Root Container */
.reported-events-page {
  /* background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); */
  min-height: 100vh;
  padding: 20px;
  position: relative;
  /* margin: 0px 150px 0px 200px;
  min-width: 2000px; */
}

.reported-events-page::before {
  content: "";
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 20% 50%,
      rgba(120, 119, 198, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(255, 119, 198, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 40% 80%,
      rgba(120, 219, 255, 0.1) 0%,
      transparent 50%
    );
  pointer-events: none;
  z-index: -1;
}

/* Floating Header Section */
.page-header {
  position: relative;
  margin-bottom: 32px;
  filter: drop-shadow(var(--shadow-medium));
}

.header-glass {
  background: var(--primary-gradient);
  border-radius: 24px;
  padding: 32px;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(20px);
  border: 1px solid var(--glass-border);
}

.header-glass::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  right: -50%;
  bottom: -50%;
  background: radial-gradient(
      circle at 70% 30%,
      rgba(255, 255, 255, 0.3) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 30% 70%,
      rgba(255, 255, 255, 0.2) 0%,
      transparent 50%
    );
  animation: rotateGradient 15s linear infinite;
  z-index: 0;
}

@keyframes rotateGradient {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.header-content {
  position: relative;
  z-index: 1;
}

.header-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.title-section {
  display: flex;
  align-items: center;
}

.icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 16px;
  margin-right: 20px;
  backdrop-filter: blur(5px);
}

.header-icon {
  color: white;
  font-size: 36px;
}

.title-content {
  color: white;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin: 4px 0 0;
  font-weight: 400;
}

/* Quick Actions */
.quick-actions {
  display: flex;
  gap: 12px;
}

.action-btn {
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

.glass-btn {
  background: rgba(255, 255, 255, 0.15) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white !important;
}

.glass-btn:hover {
  background: rgba(255, 255, 255, 0.25) !important;
  transform: translateY(-2px);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 16px;
}

.stat-card {
  position: relative;
  border-radius: 16px;
  padding: 20px;
  color: white;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  align-items: center;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-strong);
}

.glass-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.stat-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  z-index: 0;
  border-radius: 16px;
}

.stat-icon {
  font-size: 32px;
  margin-right: 20px;
  z-index: 1;
  opacity: 0.8;
}

.stat-content {
  z-index: 1;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  display: block;
  line-height: 1;
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
  display: block;
  margin-top: 4px;
}

.stat-glow {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.3) 0%,
    transparent 70%
  );
  filter: blur(10px);
  z-index: 0;
}

/* Filters Section */
.filters-section {
  margin-bottom: 24px;
}

.filters-card {
  border-radius: 16px;
  padding: 16px;
  box-shadow: var(--shadow-soft);
}

.filters-content {
  width: 100%;
}

.filters-row {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.search-container {
  flex: 1;
  min-width: 250px;
}

.modern-input {
  border-radius: 12px !important;
  overflow: hidden;
}

.modern-input::v-deep .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  border: none !important;
  box-shadow: none !important;
}

.modern-input::v-deep .v-text-field__slot input {
  color: #333 !important;
}

.modern-input::v-deep .v-label {
  color: #666 !important;
}

.modern-input::v-deep .v-icon {
  color: #666 !important;
}

.filter-select,
.sort-select {
  min-width: 180px;
}

.view-toggle {
  margin-left: auto;
}

.modern-toggle {
  background: rgba(255, 255, 255, 0.8) !important;
  border-radius: 12px !important;
  padding: 4px !important;
}

.toggle-btn {
  min-width: 40px !important;
  height: 40px !important;
  border-radius: 10px !important;
  color: #666 !important;
}

.toggle-btn.v-btn--active {
  background: var(--primary-gradient) !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

/* Content Area */
.content-area {
  margin-bottom: 32px;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  border-radius: 16px;
  text-align: center;
}

.loading-animation {
  position: relative;
  margin-bottom: 24px;
}

.loading-pulse {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: rgba(102, 126, 234, 0.2);
  animation: pulse 2s infinite ease-in-out;
  transform: scale(0.8);
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.3;
  }
  100% {
    transform: scale(0.8);
    opacity: 0.7;
  }
}

.loading-text {
  font-size: 16px;
  color: #666;
  margin-top: 16px;
  font-weight: 500;
}

/* Grid View */
.grid-view {
  width: 100%;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
}

.event-card {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  background: white;
  box-shadow: var(--shadow-soft);
}

.event-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-medium);
}

.modern-card {
  position: relative;
  overflow: hidden;
}

.card-bg-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: var(--primary-gradient);
}

.card-highlight {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.03) 0%,
    rgba(118, 75, 162, 0.03) 100%
  );
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
}

.user-info {
  display: flex;
  align-items: center;
}

.avatar-container {
  position: relative;
  margin-right: 12px;
}

.user-avatar {
  border: 2px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.avatar-ring {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border: 2px solid rgba(102, 126, 234, 0.3);
  border-radius: 50%;
  animation: pulseRing 2s infinite;
}

@keyframes pulseRing {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.user-details {
  line-height: 1.3;
}

.user-name {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.user-meta {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.type-chip {
  font-size: 10px !important;
  font-weight: 700;
  height: 18px !important;
  margin-right: 8px;
}

.modern-chip {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.post-date {
  font-size: 11px;
  color: #888;
}

.reports-badge-container {
  position: relative;
}

.reports-badge {
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.pulse-badge {
  animation: pulse 2s infinite;
}

.card-content {
  position: relative;
  z-index: 1;
  margin-bottom: 16px;
}

.event-text {
  font-size: 14px;
  color: #555;
  line-height: 1.5;
  margin-bottom: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.media-preview {
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.modern-media {
  transition: all 0.3s ease;
}

.modern-media:hover {
  transform: scale(1.02);
}

.preview-image {
  border-radius: 12px;
}

.media-count {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.glass-badge {
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
}

.reporters-preview {
  display: flex;
  align-items: center;
}

.reporters-avatars {
  display: flex;
  margin-right: 8px;
}

.reporter-avatar {
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.floating-avatar:hover {
  transform: translateY(-3px);
  z-index: 10 !important;
}

.reporters-text {
  font-size: 12px;
  color: #888;
}

.card-actions {
  display: flex;
  gap: 8px;
}

.action-icon {
  background: rgba(0, 0, 0, 0.03) !important;
  transition: all 0.3s ease;
}

.action-icon:hover {
  transform: scale(1.1);
}

.success-action:hover {
  background: rgba(76, 175, 80, 0.1) !important;
}

.danger-action:hover {
  background: rgba(244, 67, 54, 0.1) !important;
}

.danger-action {
  color: red !important;
}

/* Table View */
.table-view {
  width: 100%;
}

.table-container {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--shadow-soft);
}

.modern-table::v-deep .v-data-table__wrapper {
  border-radius: 16px;
  overflow: hidden;
}

.modern-table::v-deep table {
  background: white;
}

.modern-table::v-deep thead tr {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%);
}

.modern-table::v-deep th {
  color: #555 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modern-table::v-deep td {
  color: #444 !important;
  font-size: 14px !important;
}

.modern-table::v-deep tr:hover td {
  background: rgba(102, 126, 234, 0.05) !important;
}

.table-user {
  display: flex;
  align-items: center;
  padding: 12px;
}

.table-avatar {
  margin-right: 12px;
  border: 2px solid #f0f0f0;
}

.table-user-info {
  line-height: 1.3;
}

.table-user-name {
  font-weight: 600;
  font-size: 14px;
}

.table-type-chip {
  font-size: 10px !important;
  height: 18px !important;
  margin-top: 2px;
}

.table-content {
  max-width: 300px;
}

.table-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.table-media-indicator {
  display: flex;
  align-items: center;
  margin-top: 4px;
  font-size: 12px;
  color: #666;
}

.table-media-indicator .v-icon {
  margin-right: 4px;
  font-size: 16px;
}

.table-reports {
  display: flex;
  align-items: center;
}

.table-reports-badge {
  margin-right: 12px;
  font-weight: 700;
}

.table-reporters {
  display: flex;
  align-items: center;
}

.table-reporter-avatar {
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.more-reporters {
  font-size: 12px;
  color: #888;
  margin-left: 4px;
}

.table-date {
  line-height: 1.3;
}

.date-primary {
  font-size: 14px;
  font-weight: 500;
}

.date-secondary {
  font-size: 12px;
  color: #888;
}

.table-actions {
  display: flex;
  gap: 8px;
}

.table-action-btn {
  background: rgba(0, 0, 0, 0.03) !important;
  transition: all 0.3s ease;
}

.table-action-btn:hover {
  transform: scale(1.1);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  border-radius: 16px;
  text-align: center;
  background: white;
  box-shadow: var(--shadow-soft);
}

.empty-icon {
  position: relative;
  margin-bottom: 24px;
}

.empty-glow {
  position: absolute;
  top: -20px;
  left: -20px;
  right: -20px;
  bottom: -20px;
  background: radial-gradient(
    circle,
    rgba(102, 126, 234, 0.1) 0%,
    transparent 70%
  );
  border-radius: 50%;
  z-index: 0;
}

.empty-state h3 {
  font-size: 20px;
  color: #444;
  margin: 0 0 8px;
  font-weight: 600;
  position: relative;
  z-index: 1;
}

.empty-state p {
  font-size: 15px;
  color: #777;
  margin: 0;
  position: relative;
  z-index: 1;
  max-width: 400px;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.pagination-wrapper {
  border-radius: 16px;
  padding: 8px;
  box-shadow: var(--shadow-soft);
}

.modern-pagination::v-deep .v-pagination__item {
  background: rgba(255, 255, 255, 0.8) !important;
  color: #666 !important;
  font-weight: 600;
  min-width: 36px !important;
  height: 36px !important;
  border-radius: 10px !important;
  box-shadow: none !important;
  margin: 0 4px !important;
}

.modern-pagination::v-deep .v-pagination__item--active {
  background: var(--primary-gradient) !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
}

/* Report Details Dialog */
.modern-dialog {
  border-radius: 24px !important;
  overflow: hidden;
}

.dialog-header-modern {
  position: relative;
  padding: 24px;
  color: white;
  background: linear-gradient(365deg, #0a5b8acf 0%, #0d8abc57 100%) !important;
}

.dialog-bg-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--primary-gradient);
}

.dialog-content-header {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialog-title {
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.close-btn {
  color: white !important;
}

.dialog-body-modern {
  padding: 24px !important;
  background: #f9fafc;
}

.detail-section {
  margin-bottom: 32px;
}

.section-title-modern {
  display: flex;
  align-items: center;
  font-size: 18px;
  color: #444;
  margin-bottom: 16px;
  font-weight: 600;
}

.section-icon {
  background: rgba(102, 126, 234, 0.1);
  border-radius: 10px;
  padding: 8px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modern-detail-card {
  position: relative;
  border-radius: 16px;
  padding: 20px;
  background: white;
  box-shadow: var(--shadow-soft);
  overflow: hidden;
}

.detail-card-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.event-detail-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.detail-avatar-container {
  position: relative;
  margin-right: 16px;
}

.detail-avatar {
  border: 3px solid white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.detail-avatar-ring {
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  border: 2px solid rgba(102, 126, 234, 0.3);
  border-radius: 50%;
}

.event-detail-meta h4 {
  font-size: 18px;
  margin: 0 0 4px;
  color: #333;
}

.meta-row {
  display: flex;
  align-items: center;
}

.detail-type-chip {
  font-size: 12px !important;
  height: 22px !important;
  margin-right: 12px;
}

.detail-date {
  font-size: 13px;
  color: #888;
}

.event-detail-text {
  font-size: 15px;
  line-height: 1.6;
  color: #555;
  margin-bottom: 24px;
}

.event-detail-media {
  margin-top: 16px;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.media-item-container {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.media-item-container:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.detail-media-item {
  border-radius: 12px;
}

/* Reports List */
.reports-list {
  display: grid;
  gap: 12px;
}

.modern-report-item {
  position: relative;
  border-radius: 16px;
  padding: 16px;
  background: white;
  box-shadow: var(--shadow-soft);
  transition: all 0.3s ease;
}

.modern-report-item:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-medium);
}

.report-item-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  z-index: 0;
}

.report-detail-header {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
}

.report-avatar-container {
  margin-right: 16px;
}

.report-avatar {
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.report-detail-info h5 {
  font-size: 16px;
  margin: 0 0 4px;
  color: #333;
}

.report-detail-meta {
  display: flex;
  align-items: center;
}

.report-detail-date {
  font-size: 13px;
  color: #888;
  margin-right: 12px;
}

.reason-chip {
  font-size: 12px !important;
  height: 22px !important;
}

.modern-reason-chip {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

/* Dialog Actions */
.dialog-actions-modern {
  padding: 16px 24px !important;
  background: #f5f7fa;
  border-top: 1px solid #e0e5ec;
}

.modern-btn {
  border-radius: 12px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  padding: 8px 20px !important;
  transition: all 0.3s ease !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
}

.success-btn {
  background: var(--success-gradient) !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(0, 242, 254, 0.3) !important;
}

.danger-btn {
  /* background: linear-gradient(135deg, #f5576c 0%, #f093fb 100%) !important; */
  color: white !important;
  box-shadow: 0 4px 12px rgba(245, 87, 108, 0.3) !important;
}

/* Animations */
.card-float-move {
  transition: transform 0.5s;
}

.card-float-enter-active,
.card-float-leave-active {
  transition: all 0.5s ease;
}

.card-float-enter-from,
.card-float-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .header-glass {
    padding: 24px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

@media (max-width: 600px) {
  .reported-events-page {
    padding: 12px;
  }

  .header-glass {
    padding: 20px;
    border-radius: 20px;
  }

  .header-main {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 20px;
  }

  .quick-actions {
    margin-top: 16px;
    width: 100%;
  }

  .action-btn {
    width: 100%;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .filters-row {
    flex-direction: column;
    gap: 12px;
  }

  .search-container,
  .filter-select,
  .sort-select {
    width: 100%;
  }

  .view-toggle {
    margin-left: 0;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .modern-dialog {
    margin: 12px !important;
    max-width: calc(100% - 24px) !important;
  }

  .dialog-body-modern {
    padding: 16px !important;
  }

  .event-detail-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .detail-avatar-container {
    margin-bottom: 12px;
  }
}
</style>
